<script setup>
/**
 * BulkStageFilter — Fase 4.3
 *
 * Stage scope picker shared by all bulk-action modals (delete, set
 * priority, move, assign). Lets the operator narrow a multi-stage
 * selection to a single stage with live count of affected items.
 *
 * Default value `'all'` means "no filter" — emit consumers should send
 * `pipeline_stage: null` to the backend so the controller skips the
 * filter clause.
 *
 * Counts are recomputed reactively from `items` so the dropdown stays
 * truthful when items are checked/unchecked upstream.
 */
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  /** Full list of items (typed `Array` of card objects) being targeted. */
  items: {
    type: Array,
    required: true,
  },
  /** Pipeline stages: [{ id, name, position }, ...] sorted by position. */
  stages: {
    type: Array,
    default: () => [],
  },
  /** Currently selected stage id, or 'all'. */
  modelValue: {
    type: String,
    default: 'all',
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const open = ref(false);
const buttonRef = ref(null);
const dropdownStyle = ref({});

const itemsByStage = computed(() => {
  const map = {};
  props.items.forEach(item => {
    const s = item.pipeline_stage || item.stage || 'unknown';
    map[s] = (map[s] || 0) + 1;
  });
  return map;
});

const totalCount = computed(() => props.items.length);

const selectedLabel = computed(() => {
  if (props.modelValue === 'all') {
    return t('PIPELINE.BULK_ACTIONS.STAGE_FILTER.ALL', {
      count: totalCount.value,
    });
  }
  const stage = props.stages.find(s => s.id === props.modelValue);
  if (!stage) {
    // Stale value (stage removed) — fall back to "all"
    return t('PIPELINE.BULK_ACTIONS.STAGE_FILTER.ALL', {
      count: totalCount.value,
    });
  }
  const count = itemsByStage.value[stage.id] || 0;
  return t('PIPELINE.BULK_ACTIONS.STAGE_FILTER.ONLY_STAGE', {
    stage: stage.name,
    count,
  });
});

const toggle = () => {
  open.value = !open.value;
  if (open.value) {
    nextTick(() => {
      if (buttonRef.value) {
        const rect = buttonRef.value.getBoundingClientRect();
        dropdownStyle.value = {
          top: `${rect.bottom + 4}px`,
          left: `${rect.left}px`,
          width: `${Math.max(rect.width, 240)}px`,
        };
      }
    });
  }
};

const select = stageId => {
  emit('update:modelValue', stageId);
  open.value = false;
};

const handleOutsideClick = e => {
  if (!open.value) return;
  if (buttonRef.value && !buttonRef.value.contains(e.target)) {
    // dropdown is portaled with fixed position, so check exact target ancestry
    const dropdown = document.getElementById('bulk-stage-filter-dropdown');
    if (dropdown && !dropdown.contains(e.target)) open.value = false;
  }
};

onMounted(() => document.addEventListener('mousedown', handleOutsideClick));
onBeforeUnmount(() =>
  document.removeEventListener('mousedown', handleOutsideClick)
);
</script>

<template>
  <div class="relative">
    <label class="text-label-small font-medium text-n-slate-11 mb-1.5 block">
      {{ t('PIPELINE.BULK_ACTIONS.STAGE_FILTER.LABEL') }}
    </label>
    <button
      ref="buttonRef"
      type="button"
      class="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors text-label text-n-slate-12"
      @click="toggle"
    >
      <span class="truncate">{{ selectedLabel }}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        class="size-4 flex-shrink-0 text-n-slate-10 transition-transform"
        :class="{ 'rotate-180': open }"
        fill="currentColor"
      >
        <path
          d="M4.427 6.427a.5.5 0 0 1 .707 0L8 9.293l2.866-2.866a.5.5 0 0 1 .708.707l-3.22 3.22a.5.5 0 0 1-.708 0l-3.219-3.22a.5.5 0 0 1 0-.707z"
        />
      </svg>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        id="bulk-stage-filter-dropdown"
        :style="dropdownStyle"
        class="fixed z-50 max-h-72 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1"
      >
        <button
          type="button"
          class="w-full text-left px-3 py-2 hover:bg-n-alpha-2 transition-colors text-label text-n-slate-12 flex items-center justify-between gap-2"
          :class="{ 'bg-n-alpha-2 font-medium': modelValue === 'all' }"
          @click="select('all')"
        >
          <span>{{ t('PIPELINE.BULK_ACTIONS.STAGE_FILTER.ALL_OPTION') }}</span>
          <span class="text-label-small text-n-slate-10">{{ totalCount }}</span>
        </button>
        <div class="my-1 mx-3 border-t border-n-weak" />
        <button
          v-for="stage in stages"
          :key="stage.id"
          type="button"
          class="w-full text-left px-3 py-2 hover:bg-n-alpha-2 transition-colors text-label text-n-slate-12 flex items-center justify-between gap-2"
          :class="{ 'bg-n-alpha-2 font-medium': modelValue === stage.id }"
          :disabled="!itemsByStage[stage.id]"
          @click="itemsByStage[stage.id] && select(stage.id)"
        >
          <span class="truncate">{{ stage.name }}</span>
          <span class="text-label-small text-n-slate-10">{{
            itemsByStage[stage.id] || 0
          }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>
